/**
 * Structural equality for the plain JSON the kiosk API returns.
 *
 * The kiosk refetches the same six endpoints on every mount of the home screen.
 * Handing React a brand new object for byte-identical data is what made the
 * store's whole product grid reload several times per visit (TKT-2026-105056),
 * because object identity, not value, is what every downstream useMemo and
 * useEffect keys off. Comparing before writing state is the fix.
 *
 * Scope is deliberately small: objects, arrays, and primitives, which is all an
 * HTTP JSON body can contain. No Date/Map/Set/RegExp handling, because none of
 * these payloads carry them.
 *
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
export default function deepEqual(a, b) {
    if (a === b) return true;

    // NaN === NaN is false, but two payloads both carrying NaN are equal here.
    if (typeof a === 'number' && typeof b === 'number') return Number.isNaN(a) && Number.isNaN(b);

    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;

    const aIsArray = Array.isArray(a);
    if (aIsArray !== Array.isArray(b)) return false;

    if (aIsArray) {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i += 1) {
            if (!deepEqual(a[i], b[i])) return false;
        }
        return true;
    }

    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
        if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
        if (!deepEqual(a[key], b[key])) return false;
    }
    return true;
}
